import React from "react";
import { Box } from "rebass";
import useShop from "hooks/useShop";
import { Badge, Button, Select, Card } from "@shopify/polaris";
import { FiExternalLink } from "react-icons/fi";
import { useFormikContext } from "formik";
import get from "lodash/get";
import difference from "lodash/difference";

export default function IncompatibleOrderStatuses() {
  const { values, setFieldValue } = useFormikContext();
  const { shop } = useShop();

  const availableWcOptions = React.useMemo(() => {
    if (!shop) return;

    const availableWcOrderStatuses = shop.wc_available_order_statuses;

    return Object.entries(availableWcOrderStatuses).reduce((sum, entry) => {
      return [...sum, { value: entry[0], label: entry[1] }];
    }, []);
  }, [shop]);

  const addAdditionalIgnored = () => {
    let currentIgnored = [...(values?.wc?.incompatibleOrderStatuses || [])];
    setFieldValue("wc.incompatibleOrderStatuses", [
      ...currentIgnored,
      Object.entries(shop.wc_available_order_statuses)?.[0][0],
    ]);
  };

  const handleRemoveAdditionalIgnored = (index) => {
    let currentIgnored = [...(values?.wc?.incompatibleOrderStatuses || [])];
    currentIgnored.splice(index, 1);
    setFieldValue("wc.incompatibleOrderStatuses", currentIgnored);
  };

  return (
    <Card
      title="Ignored Order Statuses"
      primaryFooterAction={{
        onAction: () => {
          addAdditionalIgnored();
        },
        content: "Add status",
      }}
      secondaryFooterActions={[
        {
          content: "Reset to defaults",
          onAction: () => {
            setFieldValue("wc.incompatibleOrderStatuses", [
              "checkout-draft",
              "pending",
              "failed",
              "cancelled",
              "refunded",
            ]);
          },
        },
      ]}
    >
      <Card.Section>
        <Box mt={3} mb={4}>
          <p className="ri-mb-3">
            These order statuses will not trigger an ID check, even if an above
            trigger matches.
          </p>

          <p>
            <a
              href="https://woocommerce.com/document/managing-orders/order-statuses/"
              target="_blank"
            >
              Learn more&nbsp;
            </a>
            <FiExternalLink />
          </p>
        </Box>
        <div className="ri-my-4 ri-grid ri-cols-2">
          {get(values, "wc.incompatibleOrderStatuses", []).map(
            (status, index) => (
              <OrderStatusSelect
                key={index}
                index={index}
                availableWcOptions={availableWcOptions}
                values={values}
                setFieldValue={setFieldValue}
                label={`Ignored order status`}
                fieldPath={`wc.incompatibleOrderStatuses[${index}]`}
                handleRemove={handleRemoveAdditionalIgnored}
              />
            )
          )}
        </div>
        <p>Order statuses that will trigger ID checks based on your rules:</p>
        <ul className="ri-list-disc ri-list-inside">
          {difference(
            Object.keys(shop.wc_available_order_statuses),
            get(values, "wc.incompatibleOrderStatuses", [])
          ).map((status) => (
            <li key={status}>{shop?.wc_available_order_statuses?.[status]}</li>
          ))}
        </ul>
      </Card.Section>
    </Card>
  );
}

export function OrderStatusSelect({
  availableWcOptions,
  values,
  setFieldValue,
  handleRemove,
  label,
  fieldPath,
  index,
}) {
  const selectedLabel = React.useMemo(() => {
    const selectedOption = availableWcOptions.find(
      (option) => option.value === get(values, fieldPath)
    );

    return selectedOption?.label;
  }, [values, availableWcOptions]);

  if (!selectedLabel) return <></>;

  return (
    <div className="ri-my-4">
      <Select
        label={label}
        options={availableWcOptions}
        onChange={(value) => {
          setFieldValue(fieldPath, value);
        }}
        value={get(values, fieldPath)}
        helpText={
          <>
            Do not trigger an ID check if the order's status is
            <Badge>{selectedLabel}</Badge>.
          </>
        }
      />
      <Button plain onClick={() => handleRemove(index)}>
        Remove
      </Button>
    </div>
  );
}
